import {
  DatePicker as NextUIDatePicker,
  DatePickerProps as NextUIDatePickerProps,
} from "@heroui/react";
import { CalendarIcon } from "lucide-react";
import {
  CalendarDateInput,
  convertCalendarDateToUnix,
  toHeroUIDateProp,
} from "../../utils/date";
import {
  getCalendarClassNames,
  getDatePickerClassNames,
} from "../../utils/styles";
import { DateClearButton } from "../date-input/date-clear-button";
import { DateSecureIndicator } from "./date-secure-indicator";

type Color = Extract<NextUIDatePickerProps["color"], "default">;
type LabelPlacement = Extract<
  NextUIDatePickerProps["labelPlacement"],
  "outside"
>;
type Variant = Extract<NextUIDatePickerProps["variant"], "bordered">;

export type DatePickerProps = Omit<
  NextUIDatePickerProps,
  | "color"
  | "labelPlacement"
  | "maxValue"
  | "minValue"
  | "onChange"
  | "selectorButtonPlacement"
  | "selectorIcon"
  | "value"
  | "variant"
> & {
  color?: Color;
  granularity?: string;
  labelPlacement?: LabelPlacement;
  maxValue?: number;
  minValue?: number;
  isClearable?: boolean;
  isSecure?: boolean;
  onChange?: (value: number | null) => void;
  value?: number | null;
  variant?: Variant;
};

export function DatePicker({ ref, ...props }: DatePickerProps) {
  const {
    granularity,
    isClearable = true,
    isSecure = false,
    maxValue,
    minValue,
    onChange,
    value,
    ...rest
  } = props;

  const handleChange = (calendarDate: CalendarDateInput) => {
    if (!onChange) return;
    onChange(convertCalendarDateToUnix(calendarDate, granularity));
  };

  const handleClear = () => {
    onChange?.(null);
  };

  const showClearButton =
    isClearable && value != null && !rest.isDisabled && !rest.isReadOnly;
  const showSecureIndicator = isSecure;

  return (
    <NextUIDatePicker
      {...rest}
      ref={ref}
      calendarProps={{
        classNames: getCalendarClassNames(),
        visibleMonths: 1,
        weekdayStyle: "short",
      }}
      classNames={getDatePickerClassNames(props.size)}
      color="default"
      data-testid={props.name}
      labelPlacement="outside"
      maxValue={toHeroUIDateProp<NextUIDatePickerProps["maxValue"]>(
        maxValue,
        granularity,
      )}
      minValue={toHeroUIDateProp<NextUIDatePickerProps["minValue"]>(
        minValue,
        granularity,
      )}
      selectorButtonPlacement="end"
      selectorButtonProps={{ color: "primary" }}
      selectorIcon={<CalendarIcon size={16} />}
      startContent={
        showClearButton || showSecureIndicator ? (
          <>
            {showClearButton ? (
              <DateClearButton
                id={props.name ? `${props.name}-clear` : "date-picker-clear"}
                onClear={handleClear}
              />
            ) : null}
            {showSecureIndicator ? (
              <span className="order-last flex shrink-0 items-center pr-1">
                <DateSecureIndicator />
              </span>
            ) : null}
          </>
        ) : undefined
      }
      value={toHeroUIDateProp<NextUIDatePickerProps["value"]>(
        value,
        granularity,
      )}
      variant="bordered"
      onChange={handleChange}
    />
  );
}

DatePicker.displayName = "RecareUI.DatePicker";
